import React, { useState, useRef, memo } from 'react';
import { Card, Typography, Button } from 'antd';
import { InfoCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { hapticFeedbackImpactOccurred } from '@telegram-apps/sdk-react';
import { useApp } from '../context/AppContext';

const { Text } = Typography;

const SubscriptionCard = memo(({ subscription, onDelete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [translateX, setTranslateX] = useState(0);
  const [showDeleteAction, setShowDeleteAction] = useState(false);
  const navigate = useNavigate();
  const { t } = useApp();
  
  const cardRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const isDrag = useRef(false);

  const DELETE_THRESHOLD = 100; // Порог для автоматического удаления

  const handleTouchStart = (e) => {
    if (e.touches.length > 1) return;
    
    startX.current = e.touches[0].clientX;
    currentX.current = startX.current;
    isDrag.current = false;
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || e.touches.length > 1) return;
    
    e.preventDefault();
    currentX.current = e.touches[0].clientX;
    const deltaX = currentX.current - startX.current;
    
    // Разрешаем только свайп влево
    if (deltaX < 0) {
      isDrag.current = true;
      const newTranslateX = Math.max(deltaX, -DELETE_THRESHOLD);
      setTranslateX(newTranslateX);
      
      // Показываем красный фон сразу при начале свайпа
      if (!showDeleteAction) {
        setShowDeleteAction(true);
        hapticFeedbackImpactOccurred('light');
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    
    if (!isDrag.current) {
      // Если не было драга, переходим на страницу деталей
      handleShowDetails();
      return;
    }
    
    const deltaX = currentX.current - startX.current;
    
    if (Math.abs(deltaX) >= DELETE_THRESHOLD) {
      // Автоматическое удаление при достижении порога
      handleDelete();
    } else {
      // Возвращаем в исходное положение для любого свайпа меньше DELETE_THRESHOLD
      resetPosition();
    }
  };

  const resetPosition = () => {
    setTranslateX(0);
    setShowDeleteAction(false);
  };

  const handleShowDetails = () => {
    if (!isDrag.current) {
      hapticFeedbackImpactOccurred('light');
      // Переходим на страницу деталей подписки
      navigate(`/subscription/${subscription.id}`);
    }
  };

  const handleDelete = () => {
    hapticFeedbackImpactOccurred('heavy');
    onDelete(subscription.id);
    resetPosition();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(t('date_locale'), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#52c41a';
      case 'expiring_soon': return '#faad14';
      case 'expired': return '#ff4d4f';
      case 'inactive': return '#d9d9d9';
      default: return '#d9d9d9';
    }
  };

  // Получаем название страны/сервера для отображения
  const getDisplayName = () => {
    return subscription.country || subscription.server_name || t('sub_unknown_server');
  };

  // Получаем дату истечения в правильном формате
  const getExpiryDate = () => {
    return subscription.expiry_date || subscription.expiryDate;
  };

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Фон с кнопкой удаления */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: '40%',
          backgroundColor: '#ff4d4f',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: showDeleteAction ? 'none' : 'opacity 0.3s ease',
          opacity: showDeleteAction ? 1 : 0
        }}
      >
        <Button
          type="text"
          icon={<DeleteOutlined />}
          onClick={handleDelete}
          style={{
            fontSize: '24px',
            color: 'white'
          }}
        />
      </div>

      {/* Основная карточка */}
      <Card
        ref={cardRef}
        hoverable
        type="secondary"
        style={{
          cursor: 'pointer',
          margin: 0,
          transform: `translateX(${translateX}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease',
          borderLeft: `4px solid ${getStatusColor(subscription.status)}`,
          padding: 0, // убираем внутренний отступ у самого Card
        }}
        bodyStyle={{
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          justifyContent: 'space-between',
          gap: 0,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleShowDetails}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
          <Text strong style={{ fontSize: '16px' }}>
            {getDisplayName()}
          </Text>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {t('sub_until')} {formatDate(getExpiryDate())}
          </Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexShrink: 0 }}>
          <InfoCircleOutlined 
            type="primary"
            style={{
              fontSize: '24px',
            }}
          />
        </div>
      </Card>
    </div>
  );
});

SubscriptionCard.displayName = 'SubscriptionCard';

export default SubscriptionCard;
